import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import { Users, UserCheck, ShieldCheck, FileWarning, MessageSquareWarning, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next';

/**
 * HR Dashboard (v3.3) — people, onboarding, compliance, contract renewals & grievances.
 * All figures come from the self-contained /hr/* aggregates (zero new DB models). The AI
 * weekly digest is optional and degrades gracefully when AI isn't configured.
 */
const ROLE_COLORS = {
  Admin: 'bg-red-500', 'Project Manager': 'bg-blue-500', Finance: 'bg-emerald-500',
  Consultant: 'bg-indigo-500', Funder: 'bg-amber-500', HR: 'bg-purple-500', Unknown: 'bg-gray-400',
}

function StatCard({ icon: Icon, label, value, sub, tone = 'blue' }) {
  const { t } = useTranslation('dashboards');
  const tones = {
    blue: 'text-blue-600 bg-blue-50', green: 'text-emerald-600 bg-emerald-50',
    purple: 'text-purple-600 bg-purple-50', amber: 'text-amber-600 bg-amber-50', red: 'text-red-600 bg-red-50',
  }
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
        <span className={`w-7 h-7 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="w-4 h-4" /></span>
        {label}
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  )
}

function BarRow({ label, value, max, color = 'bg-blue-500' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-32 text-sm text-gray-700 truncate shrink-0">{label}</span>
      <span className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <span className={`block h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </span>
      <span className="w-10 text-right text-xs text-gray-500 tabular-nums shrink-0">{value}</span>
    </div>
  )
}

export default function HRDashboard() {
  const { t } = useTranslation('dashboards')
  const { user } = useAuth()
  const navigate = useNavigate()
  const [ov, setOv] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expiries, setExpiries] = useState([])
  const [grievances, setGrievances] = useState({ disputes: [], complaints: [] })
  const [digest, setDigest] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.get('/hr/overview')
      .then(r => { if (!cancelled) setOv(r.data) })
      .catch(() => { if (!cancelled) setOv(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    api.get('/hr/contract-expiries?days=90').then(r => !cancelled && setExpiries(r.data || [])).catch(() => {})
    api.get('/hr/grievances').then(r => !cancelled && setGrievances(r.data || { disputes: [], complaints: [] })).catch(() => {})
    api.get('/ai/hr-digest').then(r => !cancelled && setDigest(r.data)).catch(() => {})
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return <div className="p-6 text-gray-500">{t('hr_loading')}</div>
  }
  if (!ov) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
          HR data is temporarily unavailable. Please try again shortly.
        </div>
      </div>
    )
  }

  const roleEntries = Object.entries(ov.headcount.by_role || {}).sort((a, b) => b[1] - a[1])
  const roleMax = Math.max(1, ...roleEntries.map(([, v]) => v))
  const funnel = ov.onboarding_funnel || {}
  const funnelSteps = [
    ['Applied', funnel.applied], ['Under review', funnel.under_review],
    ['Onboarding', funnel.onboarding], ['Active', funnel.active], ['Suspended', funnel.suspended],
  ]
  const funnelMax = Math.max(1, ...funnelSteps.map(([, v]) => v || 0))
  const c = ov.compliance || {}

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{t('hr_title')}</h1>
            <p className="mt-1 opacity-90">People, onboarding, compliance, contracts &amp; grievances — at a glance.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/hr/people-ops')}
              className="text-sm font-medium bg-white/15 hover:bg-white/25 border border-white/30 rounded-lg px-4 py-2">
              People Ops →
            </button>
            <div className="text-left md:text-right">
              <p className="text-sm opacity-90">{t('signed_in_as')}</p>
              <p className="text-lg font-semibold">{user?.full_name || 'HR'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* AI digest */}
      {digest?.available && (digest.items?.length > 0 || digest.headline) && (
        <div className="bg-white border border-purple-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="font-semibold text-purple-800">{t('this_week_in_hr')}</span>
          </div>
          {digest.headline && <p className="text-sm text-gray-700 mb-3">{digest.headline}</p>}
          <ul className="space-y-2">
            {(digest.items || []).map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${it.severity === 'critical' ? 'bg-red-500' : it.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                <span><span className="font-medium text-gray-800">{it.title}</span> — <span className="text-gray-600">{it.detail}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Alerts */}
      {ov.alerts?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ov.alerts.map((a, i) => (
            <button key={i} onClick={() => a.link && navigate(a.link)}
              className={`text-left flex items-center gap-3 rounded-xl border px-4 py-3 transition hover:shadow-sm ${a.severity === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
              <span className="text-lg">{a.type === 'kyc' ? '🪪' : a.type === 'contract' ? '📄' : a.type === 'doc' ? '📎' : '💬'}</span>
              <span className={`text-sm font-medium ${a.severity === 'warning' ? 'text-amber-800' : 'text-blue-800'}`}>{a.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Users} label="Headcount" value={ov.headcount.total} sub={`${ov.headcount.active} active`} tone="blue" />
        <StatCard icon={UserCheck} label="In onboarding" value={(funnel.applied || 0) + (funnel.under_review || 0) + (funnel.onboarding || 0)} sub="consultants" tone="purple" />
        <StatCard icon={ShieldCheck} label="Personal details checked" value={`${c.kyc_pct || 0}%`} sub={`${c.kyc_verified || 0}/${c.kyc_total || 0}`} tone="green" />
        <StatCard icon={FileWarning} label="Contracts expiring" value={ov.contract_expiries_90d} sub="next 90 days" tone="amber" />
        <StatCard icon={MessageSquareWarning} label="Open grievances" value={ov.grievances?.total || 0} sub={`${ov.grievances?.open_disputes || 0} disputes · ${ov.grievances?.open_complaints || 0} complaints`} tone="red" />
      </div>

      {/* Headcount + onboarding funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">{t('headcount_by_role')}</h3>
          {roleEntries.map(([role, v]) => (
            <BarRow key={role} label={role} value={v} max={roleMax} color={ROLE_COLORS[role] || 'bg-gray-400'} />
          ))}
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">{t('onboarding_funnel')}</h3>
          {funnelSteps.map(([label, v]) => (
            <BarRow key={label} label={label} value={v || 0} max={funnelMax} color="bg-indigo-500" />
          ))}
        </div>
      </div>

      {/* Contract renewals + grievances */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">{t('contract_renewals')}</h3>
          {expiries.length === 0 ? (
            <p className="text-sm text-gray-400 italic">{t('no_contracts_expiring')}</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {expiries.map(e => (
                <div key={e.contract_id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{e.consultant_name || e.contract_number}</p>
                    <p className="text-xs text-gray-500 truncate">{e.contract_number}{e.project_name ? ` · ${e.project_name}` : ''}</p>
                  </div>
                  <span className={`text-xs font-semibold shrink-0 ${e.days_left != null && e.days_left <= 14 ? 'text-red-600' : 'text-amber-600'}`}>
                    {e.days_left != null ? `${e.days_left}d left` : e.end_date}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">{t('open_grievances')}</h3>
          {(grievances.disputes.length + grievances.complaints.length) === 0 ? (
            <p className="text-sm text-gray-400 italic">{t('no_grievances')}</p>
          ) : (
            <div className="space-y-2">
              {grievances.disputes.map(d => (
                <div key={`d${d.id}`} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
                  <span className="text-sm text-gray-800 truncate">⚖️ {d.subject}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">{d.status}</span>
                </div>
              ))}
              {grievances.complaints.map(cp => (
                <div key={`c${cp.id}`} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
                  <span className="text-sm text-gray-800 truncate">📣 {cp.complaint_number} — {cp.name}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 shrink-0">{cp.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
